import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { Button, Popover } from "antd";
import { BetaSchemaForm, ProFormColumnsType, ProFormInstance } from '@ant-design/pro-components';
import { IconFont } from "../IconFont";
import './style.less'

interface InlineEditProps {
  value: string | number;
  onSave: (val: Record<string, any>) => void | Promise<void>;
  width?: number;
  textAlign?: 'left' | 'center' | 'right';
  className?: string;
  column: ProFormColumnsType
}

export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  width = 120,
  textAlign = 'right',
  className = '',
  column
}) => {
  const [visible, setVisible] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);
  const [inputWidth, setInputWidth] = useState(width);
  const [loading, setLoading] = useState(false);
  const [renderKey, setRenderKey] = useState(0); // 用于强制重新渲染

  const triggerRef = useRef<HTMLSpanElement>(null);
  const formRef = useRef<ProFormInstance>(null);

  // 取消编辑
  const handleCancel = useCallback(() => {
    setVisible(false);
    // 重置表单值
    // if (column?.dataIndex) {
    //   const dataIndex = column.dataIndex as string;
    //   formRef?.current?.setFieldsValue({ [dataIndex]: value });
    // }
  }, [column?.dataIndex, value]);

  // 触发表单提交
  const handleSubmit = useCallback(() => {
    formRef.current?.submit();
  }, []);

  // 表单提交处理
  const handleFinish = useCallback((values: Record<string, any>) => {
    if (column.dataIndex) {
      // 设置异步提交
      setTimeout(async () => {
        try {
          setLoading(true);
          await onSave(values);
          setVisible(false);
        } catch (error) {
          console.error('保存失败:', error);
        } finally {
          setLoading(false);
        }
      }, 0);
    }
  }, [column.dataIndex, value, onSave]);

  // 处理 column 配置，添加回车提交和样式
  const processedColumn: ProFormColumnsType = {
    ...column,
    formItemProps: {
      style: { marginBottom: 0 },
      ...column.formItemProps,
    },
    fieldProps: {
      size: 'small',
      autoFocus: true, // 自动聚焦
      onPressEnter: (e: any) => {
        e.preventDefault();
        handleSubmit();
      },
      ...column.fieldProps,
    }
  };

  // 打开时测量宽度并自动 focus
  useLayoutEffect(() => {
    if (visible && triggerRef.current) {
      const width = triggerRef.current.offsetWidth;
      setInputWidth(Math.max(width, 100));
      // 设置表单初始值
      // if (column.dataIndex) {
      //   const dataIndex = column.dataIndex as string;
      //   formRef?.current?.setFieldsValue({ [dataIndex]: value });
      // }

      // 延迟聚焦，确保 Popover 和表单完全渲染
      const timer = setTimeout(() => {
        // try {
        //   // 方法1: 通过 formRef 获取字段实例
        //   if (column.dataIndex && formRef.current) {
        //     const fieldInstance = formRef.current.getFieldInstance?.(column.dataIndex as string);
        //     if (fieldInstance?.focus) {
        //       fieldInstance.focus();
        //       fieldInstance.select?.();
        //       return;
        //     }
        //   }

        //   // 方法2: 通过 DOM 查询
        //   const popoverContent = document.querySelector('.inline-edit-popover');
        //   if (popoverContent) {
        //     const input = popoverContent.querySelector('input, textarea, select') as HTMLInputElement;
        //     if (input) {
        //       input.focus();
        //       input.select?.();
        //     }
        //   }
        // } catch (error) {
        //   console.warn('聚焦失败:', error);
        // }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [visible, value, column.dataIndex]);

  // 监听 ESC 键取消编辑
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        handleCancel();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, handleCancel]);

  const handleOpenChange = (open: boolean) => {
    if (open && !hasRendered) setHasRendered(true);
    if (open) {
      setRenderKey(prev => prev + 1); // 每次打开时更新 key
    }
    setVisible(open);
  };

  // Popover 内容第一次渲染必须存在，否则弹出位置不计算
  const content = (
    <div className='flex items-center justify-between'>
      <BetaSchemaForm
        key={renderKey} // 使用递增的 key 确保每次打开都重新渲染
        formRef={formRef}
        layoutType='Form'
        columns={[processedColumn]}
        submitter={false}
        onFinish={handleFinish}
        style={{ width: inputWidth }}
        disabled={loading}
      />
      <span className="inline-edit-icon flex-center">
        <IconFont
          type="icon-remove"
          onClick={handleCancel}
          style={{ opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
        />
        <IconFont
          type="icon-checkmark-2-small"
          onClick={handleSubmit}
          style={{ opacity: loading ? 0.5 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
        />
      </span>
    </div>
  );

  return (
    <div className="inline-edit-container">
      <span
        className={`inline-block ${className}`}
        style={{ textAlign }}
        ref={triggerRef}
      >
        {value}
      </span>
      <Popover
        rootClassName="inline-edit-popover"
        content={content}
        trigger="click"
        open={visible}
        onOpenChange={handleOpenChange}
        placement="bottom"
      >
        <Button color="default" variant="text" size="small">
          <IconFont type="icon-edit" />
        </Button>
      </Popover>
    </div>

  );
};
